import React, { forwardRef } from 'react';

// Botón de solo ícono compartido de VLS (auditoría de UI 2026-10-20, Lote 0).
// Hoy hay botones de ícono de 14 a 36 px sin nombre accesible en crm,
// comisiones, visitas, proyectos, préstamos, alineaciones (50 casos),
// incidentes y QB, y la escala doble de algunas apps (h-8 = 64 px) confunde
// los tamaños. Este botón:
//   - EXIGE un nombre: `label` (o `aria-label`). Va como aria-label y como
//     title (el tooltip del mouse). Sin nombre avisa en consola.
//   - Mide 44 x 44 px de área táctil SIEMPRE, en px fijos (no con la escala
//     de Tailwind). Con tamano="sm" el fondo visible es de 32 px, pero el
//     área que recibe el toque sigue siendo de 44 px.
//
// ─── Migración (mecánica) ────────────────────────────────────────────────
//   ANTES:
//     <button onClick={quitar} className="p-1 text-red-600 hover:bg-red-50 rounded">
//       <X size={14} />
//     </button>
//   DESPUÉS:
//     <BotonIcono label={`Quitar ${tour.nombre}`} variante="peligro" onClick={quitar}>
//       <X size={18} />
//     </BotonIcono>
//
// Props: label (obligatorio salvo que venga aria-label), children (el ícono;
// se marca aria-hidden), onClick, variante 'neutro' (por defecto) |
// 'primario' | 'peligro', tamano 'md' (44 px visibles) | 'sm' (32 px
// visibles en 44 px de toque), presionado (boolean: aria-pressed, para
// alternar), disabled, type ('button' por defecto, nunca envía un form por
// accidente), sinTitle (no pone title), className (solo layout), y el resto
// pasa al <button> (data-*, aria-controls, aria-expanded…). `ref` apunta al
// <button>.
// Sin dependencias; colores por var(--color-*) con respaldo.

const VARIANTES = {
  neutro: { color: 'var(--color-foreground, #1C1917)', hover: 'hover:bg-black/5' },
  primario: { color: 'var(--color-primary, #0F7A3D)', hover: 'hover:bg-black/5' },
  peligro: { color: 'var(--color-destructive, #DC2626)', hover: 'hover:bg-black/5' },
};

const BotonIcono = forwardRef(function BotonIcono({
  label, 'aria-label': ariaLabel, children, variante = 'neutro', tamano = 'md', presionado,
  type = 'button', sinTitle = false, className = '', style, ...resto
}, ref) {
  const nombre = (ariaLabel ?? label ?? '').toString().trim();
  if (!nombre && typeof console !== 'undefined') {
    console.error('[BotonIcono] falta `label`: un botón de solo ícono necesita un nombre que el lector de pantalla pueda leer.');
  }
  const v = VARIANTES[variante] || VARIANTES.neutro;
  const visible = tamano === 'sm' ? 32 : 44;
  return (
    <button
      ref={ref}
      type={type}
      aria-label={nombre || undefined}
      title={sinTitle || !nombre ? undefined : nombre}
      aria-pressed={typeof presionado === 'boolean' ? presionado : undefined}
      data-vls-boton-icono={tamano}
      className={`group relative inline-flex items-center justify-center rounded-lg disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${tamano === 'sm' ? '' : v.hover} ${className}`}
      style={{ width: 44, height: 44, minWidth: 44, minHeight: 44, padding: 0, flexShrink: 0, color: v.color, background: 'transparent', border: 0, ...style }}
      {...resto}
    >
      <span
        aria-hidden="true"
        className={`inline-flex items-center justify-center rounded-lg ${tamano === 'sm' ? 'group-hover:bg-black/5' : ''} ${presionado ? 'bg-black/5' : ''}`}
        style={{ width: visible, height: visible, pointerEvents: 'none' }}
      >
        {children}
      </span>
    </button>
  );
});

export default BotonIcono;
